/**
 * Fund Card API response parsers
 * Transform API responses to app-friendly format
 */

/* ================================
   Risk–Return Matrix (Standard Deviation vs Mean)
================================ */
/**
 * Parse risk-return matrix API response
 * 
 * API Response Structure:
 * {
 *   "code": "ADCRRM0003",
 *   "statusCode": 200,
 *   "message": "Fetched risk return matrix successfully",
 *   "result": {
 *     "data": [
 *       {
 *         "fundId": "INF200KA1Y65",
 *         "fundSize": 393374103000,
 *         "fundReturn": 13.27,  // Mean value (will be multiplied by 100 for percentage display)
 *         "fundRisk": 6.182,     // Standard Deviation
 *         "name": "Fund Name"    // Future: Fund name field (optional, will be added by backend)
 *       }
 *     ]
 *   },
 *   "requestId": "req-..."
 * }
 * 
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed response with normalized fund data
 */
export const parseRiskReturnMatrixResponse = (response) => {
  const asOnDate = response?.result?.as_on_date ?? null;

  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch risk-return matrix",
      code: response?.code ?? "UNKNOWN_ERROR",
      funds: [],
      as_on_date: null,
      requestId: response?.requestId ?? null,
    };
  }

  const fundsData = Array.isArray(response?.result?.data)
    ? response?.result?.data
    : [];

  const allSizes =
    fundsData
      ?.map((f) => {
        const size = parseFloat(f?.fundSize ?? 0);
        return isNaN(size) ? 0 : size;
      })
      .filter((s) => s > 0) ?? [];

  const minSize = allSizes.length ? Math.min(...allSizes) : 0;
  const maxSize = allSizes.length ? Math.max(...allSizes) : 0;

  const normalizeSize = (size) => {
    if (!size || maxSize === minSize) return 50;
    return Math.round(20 + ((size - minSize) / (maxSize - minSize)) * 80);
  };

  const parsedFunds =
    fundsData
      ?.map((fund) => {
        if (!fund) return null;

        const risk = parseFloat(fund?.fundRisk);
        const ret = parseFloat(fund?.fundReturn);
        const size = parseFloat(fund?.fundSize);

        if ([risk, ret, size].some(isNaN)) return null;

        return {
          fundId: fund?.fundId ?? null,
          fundName: fund?.name ?? null,
          risk,
          return: ret, // Keep original value with decimal points (e.g., 14.76, 15.32)
          size: normalizeSize(size),
        };
      })
      .filter(Boolean) ?? [];

  return {
    success: true,
    message: response?.message ?? "Risk-return matrix fetched successfully",
    code: response?.code ?? null,

    // ✅ Added
    as_on_date: asOnDate,

    funds: parsedFunds,
    requestId: response?.requestId ?? null,
  };
  console.log("parseRiskReturnMatrixResponse", response);
};


/* ================================
   Historical Returns
================================ */
export const parseHistoricalReturnsResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch historical returns",
      code: response?.code ?? "UNKNOWN_ERROR",
      data: [],
      nav: null,
      navDate: null,
      as_on_date: null,
    };
  }

  const data = Array.isArray(response?.result?.data)
    ? response.result.data
    : [];

  const parsedData = data.map((item, index) => {
    ;
    const date = item?.date
      ? new Date(item.date).toISOString().split('T')[0]
      : '';
    // Parse nav (value1) - This fund
    const navValue = item?.nav != null ? Number(item.nav) : null;
    const value1 = navValue != null && !isNaN(navValue) ? navValue : 0;

    // Parse catAvg (value2) - Category Average (Yellow line)
    const catAvgValue = item?.catAvg != null ? Number(item.catAvg) : null;
    const value2 = catAvgValue != null && !isNaN(catAvgValue) ? catAvgValue : null;

    // Parse indi (value3) - Indices
    const indiValue = item?.indi != null ? Number(item.indi) : null;
    const value3 = indiValue != null && !isNaN(indiValue) ? indiValue : null;
    return {
      date,
      value1,
      value2,
      value3,
    };
  });

  const latest = data.reduce((a, b) =>
    new Date(b?.date) > new Date(a?.date) ? b : a,
    {}
  );

  return {
    success: true,
    message: response?.message ?? "Historical returns fetched successfully",
    code: response?.code ?? null,
    data: parsedData,
    nav: latest?.nav ? Number(latest.nav) : null,
    navDate: latest?.date ?? null,
    as_on_date: response?.result?.as_on_date ?? null,
    requestId: response?.requestId ?? null,
  };
};

/* ================================
   Growth of 10,000
================================ */
export const parseGrowthOfTenThousandResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch growth of 10,000",
      code: response?.code ?? "UNKNOWN_ERROR",
      data: [],
      asOnDate: null,   // 👈 added
      requestId: response?.requestId ?? null,
    };
  }

  const data = Array.isArray(response?.result?.data)
    ? response.result.data
    : [];

  return {
    success: true,
    message: response?.message ?? "Growth of 10,000 fetched successfully",
    code: response?.code ?? null,
    data: data.map((item, index) => ({
      date: item?.date ? new Date(item.date).getTime() || index : index,
      value1: Number(item?.value) || 0,
      value2: item?.catAvg != null ? Number(item.catAvg) : null,
      value3: item?.indi != null ? Number(item.indi) : null,
    })),
    asOnDate: response?.result?.as_on_date ?? null,   // 👈 added here
    requestId: response?.requestId ?? null,
  };
};


/* ================================
   Peer Comparison
================================ */
export const parsePeerComparisonResponse = (response) => {
  if (!response?.result) {
    const errorResponse = {
      success: false,
      message: response?.message ?? "Failed to fetch peer comparison",
      code: response?.code ?? "UNKNOWN_ERROR",
      data: [],
      asOnDate: null,
      requestId: response?.requestId ?? null,
    };

    // console.log("📉 Peer Comparison Parser (Error):", errorResponse);
    return errorResponse;
  }

  const data = Array.isArray(response?.result?.data)
    ? response.result.data
    : [];

  const parsedResponse = {
    success: true,
    message: response?.message ?? "Peer comparison fetched successfully",
    code: response?.code ?? null,

    // ✅ Added as_on_date here
    asOnDate: response?.result?.as_on_date ?? null,

    data: data.map((fund) => {
      let trend = null;

      // Option 1: privNav array
      if (Array.isArray(fund?.privNav) && fund.privNav.length >= 3) {
        const navValues = fund.privNav.slice(-3).map((n) => {
          if (typeof n === "object" && n?.nav != null) {
            return Number(n.nav) || 0;
          }
          return Number(n) || 0;
        });
        if (navValues.some((v) => v > 0)) trend = navValues;
      }

      // Option 2: historicalNav
      if (!trend && Array.isArray(fund?.historicalNav) && fund.historicalNav.length >= 3) {
        const navValues = fund.historicalNav.slice(-3).map((n) => {
          if (typeof n === "object" && n?.nav != null) {
            return Number(n.nav) || 0;
          }
          return Number(n) || 0;
        });
        if (navValues.some((v) => v > 0)) trend = navValues;
      }

      // Option 3: navHistory
      if (!trend && Array.isArray(fund?.navHistory) && fund.navHistory.length >= 3) {
        const navValues = fund.navHistory.slice(-3).map((n) => {
          if (typeof n === "object" && n?.nav != null) {
            return Number(n.nav) || 0;
          }
          return Number(n) || 0;
        });
        if (navValues.some((v) => v > 0)) trend = navValues;
      }

      // Option 4: last3DaysNav
      if (!trend && Array.isArray(fund?.last3DaysNav) && fund.last3DaysNav.length === 3) {
        const navValues = fund.last3DaysNav.map((n) => Number(n) || 0);
        if (navValues.some((v) => v > 0)) trend = navValues;
      }

      // Fallback
      if (!trend) {
        const currentNav = Number(fund?.nav) || 0;
        const navChangePercent = Number(fund?.navChangePercent) || 0;

        if (currentNav > 0) {
          const prevNav =
            navChangePercent !== 0
              ? currentNav / (1 + navChangePercent / 100)
              : currentNav;

          trend = Math.abs(prevNav - currentNav) > 0.01
            ? [prevNav, prevNav, currentNav]
            : [currentNav * 0.99, currentNav, currentNav * 1.01];
        }
      }

      return {
        id: fund?.fundId ?? fund?.id ?? null,
        fundId: fund?.fundId ?? fund?.id ?? null,
        mutualFund: fund?.name ?? "",
        rating: fund?.rating ?? 0,
        fundScore: fund?.inxitsScore ?? 0,
        nav: `₹${Number(fund?.nav ?? 0).toFixed(2)}`,
        navChange: {
          value: Number(fund?.navChangeValue) || 0,
          percentage: Number(fund?.navChangePercent) || 0,
        },
        trendLine: trend && trend.length === 3 ? trend : null,
      };
    }),

    requestId: response?.requestId ?? null,
  };

  // ✅ Console the final parsed response
  // console.log("📊 Peer Comparison Parser (Success):", parsedResponse);

  return parsedResponse;
};


/* ================================
   Holdings
================================ */
const toNumber = (val) => {
  const num = Number(val);
  return Number.isNaN(num) ? null : num;
};

export const parseHoldingsResponse = (response) => {
  // Extract as_on_date safely from result
  const asOnDate = response?.result?.as_on_date ?? null;

  if (!response?.result) {
    const errorResponse = {
      success: false,
      message: response?.message ?? "Failed to fetch holdings",
      code: response?.code ?? "UNKNOWN_ERROR",
      holdings: [],
      monthwise: [],
      as_on_date: null,
      requestId: response?.requestId ?? null,
    };

    return errorResponse;
  }

  const monthwiseData = response?.result?.data?.monthwise;

  // ==========================
  // ✅ NEW MONTHWISE STRUCTURE
  // ==========================
  if (Array.isArray(monthwiseData) && monthwiseData.length > 0) {
    const monthwise = monthwiseData.map((month) => {
      const rawHoldings = Array.isArray(month?.data?.data)
        ? month.data.data
        : [];

      const holdings = rawHoldings
        .filter((h) => h && h.id != null)
        .map((h) => ({
          id: h?.id ?? null,
          name: h?.name ?? null,
          ticker: h?.ticker ?? null,
          isin: h?.ISIN ?? null,
          holdingIsin: h?.holding_isin ?? null,
          sector: h?.sector ?? null,
          portfolioWeighting: h?.portfolio_weighting,
          marketValue: toNumber(h?.position_market_value),
          shares: toNumber(h?.shares),
          holdingType: h?.holding_type ?? null,
          detailHoldingType: h?.detail_holding_type ?? null,
          asOnDate: h?.as_on_date ?? null,
        }));

      return {
        label: month?.label ?? "",
        value: month?.value ?? null,
        aum: month?.aum ?? null,
        holdings,
      };
    });

    // Backward compatibility
    const flattenedHoldings = monthwise.flatMap((m) => m.holdings);

    const parsedResponse = {
      success: true,
      message: response?.message ?? "Fund holdings retrieved successfully",
      code: response?.code ?? null,
      holdings: flattenedHoldings,
      monthwise,

      // ✅ Guaranteed root-level as_on_date
      as_on_date: asOnDate,

      requestId: response?.requestId ?? null,
    };

    // console.log("📊 Holdings Parser (Monthwise):", parsedResponse);
    return parsedResponse;
  }

  // ==========================
  // ⛔ OLD / LEGACY / EMPTY DATA
  // ==========================
  const data = Array.isArray(response?.result?.data)
    ? response.result.data
    : [];

  const holdings = data
    .filter((h) => h && h.id != null)
    .map((h) => ({
      id: h?.id ?? null,
      name: h?.name ?? null,
      isin: h?.ISIN ?? null,
      sector: h?.sector ?? null,
      portfolioWeighting: toNumber(h?.portfolio_weighting),
      marketValue: toNumber(h?.position_market_value),
      shares: toNumber(h?.shares),
      holdingType: h?.holding_type ?? null,
    }));

  const parsedResponse = {
    success: true,
    message: response?.message ?? "Fund holdings retrieved successfully",
    code: response?.code ?? null,
    holdings,
    monthwise: [],

    // ✅ Even when data = {}
    as_on_date: asOnDate,

    requestId: response?.requestId ?? null,
  };

  // console.log("📊 Holdings Parser (Flat/Empty):", parsedResponse);
  return parsedResponse;
};



/* ================================
   Risk Return Statistics
================================ */
export const parseRiskReturnStatisticsResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch risk return statistics",
      code: response?.code ?? "UNKNOWN_ERROR",
      ratios: [],
      as_on_date: null,
    };
  }

  const ratios = Array.isArray(response?.result?.ratios)
    ? response.result.ratios
    : [];

  // Extract as_on_date from API response
  // API structure: result.data.as_on_date
  const as_on_date = response?.result?.as_on_date ?? null;

  return {
    success: true,
    message: response?.message ?? "Risk return statistics fetched successfully",
    code: response?.code ?? null,
    as_on_date: response?.result?.as_on_date ?? null,
    ratios: ratios.map((r, index) => {
      // Null checks and optional chaining for all fields
      const name = r?.name ?? '';
      const rating = r?.rating ?? null;
      const fundValue = r?.fund;
      const categoryValue = r?.category;

      return {
        id: index + 1,
        ratio: name,
        rating: rating != null && !isNaN(Number(rating)) && Number(rating) > 0
          ? Number(rating)
          : '-',
        fund: fundValue != null && !isNaN(Number(fundValue))
          ? `${Number(fundValue).toFixed(2)}%`
          : '-',
        category: categoryValue != null && !isNaN(Number(categoryValue))
          ? `${Number(categoryValue).toFixed(2)}%`
          : '-',
      };
    }),
  };
};

/* ================================
   Sector Allocation
================================ */
export const parseSectorAllocationResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch sector allocation",
      code: response?.code ?? "UNKNOWN_ERROR",
      sectors: [],
      monthwise: [],
    };
  }

  // Optional label prettifier
  const labelMap = {
    financialServices: "Financial Services",
    technology: "Technology",
    energy: "Energy",
    consumerCyclical: "Consumer Cyclical",
    consumerDefensive: "Consumer Defensive",
    industrials: "Industrials",
    healthcare: "Healthcare",
    basicMaterials: "Basic Materials",
    communicationServices: "Communication Services",
    utilities: "Utilities",
    realEstate: "Real Estate",
  };

  // Filter out non-sector fields (id, asOnDate, etc.)
  const sectorFields = [
    'basicMaterials',
    'communicationServices',
    'consumerCyclical',
    'consumerDefensive',
    'energy',
    'financialServices',
    'healthcare',
    'industrials',
    'realEstate',
    'technology',
    'utilities',
  ];

  // Helper function to process sector data
  const processSectorData = (sectorData) => {
    // Create clean raw data with only sector fields
    const cleanRawData = {};
    const mappedData = {};
    const categoryAverages = {}; // Store category averages by sector field name

    sectorFields.forEach((field) => {
      const value = sectorData?.[field];
      const numericValue = value != null && !isNaN(Number(value)) ? Number(value) : 0;
      cleanRawData[field] = numericValue;
      mappedData[labelMap?.[field] || field] = numericValue;

      // Extract category average (field name + "Cat" suffix, e.g., basicMaterialsCat)
      const catField = `${field}Cat`;
      const catValue = sectorData?.[catField];
      const catNumericValue = catValue != null && !isNaN(Number(catValue)) ? Number(catValue) : null;
      if (catNumericValue != null) {
        categoryAverages[field] = catNumericValue;
      }
    });

    const mapped = Object.entries(cleanRawData || {})
      .map(([key, rawValue]) => {
        const numValue = Number(rawValue);
        if (rawValue == null || Number.isNaN(numValue) || numValue === 0) return null;
        return {
          label: labelMap?.[key] ?? key,
          value: numValue,
          categoryAverage: categoryAverages[key] ?? null, // Include category average if available
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    return {
      sectors: mapped, // Top 6 sectors for display
      rawData: cleanRawData, // Clean raw data object with only sector fields
      mappedData, // Raw data with human-readable labels as keys
    };
  };

  // Check if response has monthwise structure
  const monthwiseData = response?.result?.data?.monthwise;

  if (Array.isArray(monthwiseData) && monthwiseData.length > 0) {
    // New structure with monthwise data
    // Process each month and sort by timestamp descending (latest first)
    const monthwise = monthwiseData
      .map((month) => {
        if (!month) return null;
        const sectorData = month?.data || {};
        const processed = processSectorData(sectorData);

        return {
          label: month?.label ?? '',
          value: month?.value ?? null, // timestamp
          id: sectorData?.id ?? null,
          asOnDate: sectorData?.asOnDate ?? null,
          sectors: processed.sectors,
          rawData: processed.rawData,
          mappedData: processed.mappedData,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        // Sort by timestamp descending (latest first)
        const valA = a.value || 0;
        const valB = b.value || 0;
        return valB - valA;
      });

    // Use latest month's data for backward compatibility (first item after sorting)
    const latestMonth = monthwise[0] || {};
    const latestSectors = latestMonth.sectors || [];

    return {
      success: true,
      message: response?.message ?? "Sector allocation fetched successfully",
      code: response?.code ?? null,
      sectors: latestSectors, // Top 6 sectors from latest month for backward compatibility
      rawData: latestMonth.rawData || {}, // Latest month's raw data
      mappedData: latestMonth.mappedData || {}, // Latest month's mapped data
      monthwise: monthwise, // Monthly structured data
      requestId: response?.requestId ?? null,
    };
  }

  // Fallback to old structure (flat array)
  const sectorsData = Array.isArray(response?.result?.data)
    ? response?.result?.data
    : [];

  const firstEntry = sectorsData?.[0] ?? {};
  const processed = processSectorData(firstEntry);

  return {
    success: true,
    message: response?.message ?? "Sector allocation fetched successfully",
    code: response?.code ?? null,
    sectors: processed.sectors, // Top 6 sectors for display
    rawData: processed.rawData, // Clean raw data object with only sector fields
    mappedData: processed.mappedData, // Mapped data for the single entry
    monthwise: [],
    requestId: response?.requestId ?? null,
  };
};

/* ================================
   Error Parser (SINGLE SOURCE)
================================ */
export const parseFundCardErrorResponse = (error) => {
  const data = error?.data;

  return {
    success: false,
    message: data?.message ?? data?.error ?? "An error occurred",
    code: data?.code ?? `HTTP_${error?.status ?? "UNKNOWN"}`,
    details: data?.details ?? null,
    status: error?.status ?? 500,
  };
};

/* ================================
   Metrics Overview
================================ */
export const parseMetricsOverviewResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch metrics overview",
      code: response?.code ?? "UNKNOWN_ERROR",
      portfolioMetrics: [],
      fundamentals: [],
      asOnDate: null,
      requestId: response?.requestId ?? null,
      priorityRiskClass: null,
      schemeCategoryId: null,
    };
  }

  const portfolioMetrics = Array.isArray(response?.result?.portfolioMetrics)
    ? response.result.portfolioMetrics.map((item) => ({
      id: item?.name || Math.random(),
      ratio: item?.name || '',
      fund: item?.fund !== null && item?.fund !== undefined ? parseFloat(item.fund) : null,
      min: item?.min !== null && item?.min !== undefined ? parseFloat(item.min) : null,
      avg: item?.avg !== null && item?.avg !== undefined ? parseFloat(item.avg) : null,
      max: item?.max !== null && item?.max !== undefined ? parseFloat(item.max) : null,
    }))
    : [];

  const fundamentals = Array.isArray(response?.result?.fundamentals)
    ? response.result.fundamentals.map((item) => ({
      id: item?.name || Math.random(),
      ratio: item?.name || '',
      fund: item?.fund !== null && item?.fund !== undefined ? parseFloat(item.fund) : null,
      min: item?.min !== null && item?.min !== undefined ? parseFloat(item.min) : null,
      avg: item?.avg !== null && item?.avg !== undefined ? parseFloat(item.avg) : null,
      max: item?.max !== null && item?.max !== undefined ? parseFloat(item.max) : null,
    }))
    : [];

  return {
    success: true,
    message: response?.message ?? "Metrics overview fetched successfully",
    code: response?.code ?? null,
    requestId: response?.requestId ?? null,
    asOnDate: response?.result?.as_on_date ?? null,
    portfolioMetrics,
    fundamentals,
    priorityRiskClass: response?.result?.PriorityRiskClass ?? null,
    schemeCategoryId: response?.result?.scheme_category_id ?? null,
  };
};


/* ================================
   Equity Analysis
================================ */
export const parseEquityAnalysisResponse = (response) => {
  const asOnDate = response?.result?.as_on_date ?? null;

  if (!response?.result?.data || !Array.isArray(response.result.data)) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch equity analysis",
      code: response?.code ?? "UNKNOWN_ERROR",
      dataByYear: {
        "1Y": [],
        "3Y": [],
        "5Y": [],
      },

      // ✅ Added
      as_on_date: null,

      requestId: response?.requestId ?? null,
    };
  }
  // console.log(response,'response')
  // console.log(periodData,'periodData');
  const transformPeriodData = (periodData) => {
    const categories = [];

    if (periodData.alpha) {
      const alphaData = periodData.alpha;
      categories.push({
        category: "Alpha",
        min: parseFloat(alphaData.categoryMin) || 0,
        max: parseFloat(alphaData.categoryMax) || 0,
        avg: parseFloat(alphaData.categoryAverage) || 0,
        current: parseFloat(alphaData.fundValue) || 0,
      });
    }

    if (periodData.mean) {
      const mean = periodData.mean;
      categories.push({
        category: "Mean",
        min: parseFloat(mean.categoryMin) || 0,
        max: parseFloat(mean.categoryMax) || 0,
        avg: parseFloat(mean.categoryAverage) || 0,
        current: parseFloat(mean.fundValue) || 0,
      });
    }

    if (periodData.rSquared) {
      const r2 = periodData.rSquared;
      categories.push({
        category: "R²",
        min: parseFloat(r2.categoryMin) || 0,
        max: parseFloat(r2.categoryMax) || 0,
        avg: parseFloat(r2.categoryAverage) || 0,
        current: parseFloat(r2.fundValue) || 0,
      });
    }

    return categories;
  };

  const dataByYear = {
    "1Y": [],
    "3Y": [],
    "5Y": [],
  };

  response.result.data.forEach((item) => {
    const period = item.period?.toLowerCase();
    const transformedData = transformPeriodData(item);

    if (period === "1y") {
      dataByYear["1Y"] = transformedData;
    } else if (period === "3y") {
      dataByYear["3Y"] = transformedData;
    } else if (period === "5y") {
      dataByYear["5Y"] = transformedData;
    }
  });

  return {
    success: true,
    message: response?.message ?? "Fetched equity analysis successfully",
    code: response?.code ?? null,

    // ✅ Added
    as_on_date: asOnDate,

    dataByYear,
    requestId: response?.requestId ?? null,
  };
};


/* ================================
   Risk Ratios
================================ */
export const parseRiskRatiosResponse = (response) => {
  console.log("parseRiskRatiosResponse", response);
  const asOnDate = response?.result?.as_on_date ?? null;

  if (!response?.result?.data || !Array.isArray(response.result.data)) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch risk ratios",
      code: response?.code ?? "UNKNOWN_ERROR",
      dataByYear: {
        "1Y": [],
        "3Y": [],
        "5Y": [],
      },

      // ✅ Always present
      as_on_date: null,

      requestId: response?.requestId ?? null,
    };
  }

  const transformPeriodData = (periodData) => {
    const categories = [];

    // Helper function to preserve null values and convert to number only if not null
    const toNumberOrNull = (value) => {
      if (value === null || value === undefined) return null;
      const num = typeof value === 'number' ? value : parseFloat(value);
      return isNaN(num) ? null : num;
    };

    if (periodData.beta) {
      const beta = periodData.beta;
      categories.push({
        category: "Beta",
        min: toNumberOrNull(beta.categoryMin),
        max: toNumberOrNull(beta.categoryMax),
        avg: toNumberOrNull(beta.categoryAverage),
        current: toNumberOrNull(beta.fundValue),
      });
    }

    if (periodData.standardDeviation) {
      const sd = periodData.standardDeviation;
      categories.push({
        category: "Standard Deviation",
        min: toNumberOrNull(sd.categoryMin),
        max: toNumberOrNull(sd.categoryMax),
        avg: toNumberOrNull(sd.categoryAverage),
        current: toNumberOrNull(sd.fundValue),
      });
    }

    if (periodData.maxDrawdown) {
      const md = periodData.maxDrawdown;
      categories.push({
        category: "Max Drawdown",
        min: toNumberOrNull(md.categoryMin),
        max: toNumberOrNull(md.categoryMax),
        avg: toNumberOrNull(md.categoryAverage),
        current: toNumberOrNull(md.fundValue),
      });
    }

    if (periodData.captureUpside) {
      const uc = periodData.captureUpside;
      categories.push({
        category: "Upside Capture",
        min: toNumberOrNull(uc.categoryMin),
        max: toNumberOrNull(uc.categoryMax),
        avg: toNumberOrNull(uc.categoryAverage),
        current: toNumberOrNull(uc.fundValue),
      });
    }

    if (periodData.captureDownside) {
      const dc = periodData.captureDownside;
      categories.push({
        category: "Downside Capture",
        min: toNumberOrNull(dc.categoryMin),
        max: toNumberOrNull(dc.categoryMax),
        avg: toNumberOrNull(dc.categoryAverage),
        current: toNumberOrNull(dc.fundValue),
      });
    }

    if (periodData.trackingError) {
      const te = periodData.trackingError;
      categories.push({
        category: "Tracking Error",
        min: toNumberOrNull(te.categoryMin),
        max: toNumberOrNull(te.categoryMax),
        avg: toNumberOrNull(te.categoryAverage),
        current: toNumberOrNull(te.fundValue),
      });
    }

    return categories;
  };

  const dataByYear = {
    "1Y": [],
    "3Y": [],
    "5Y": [],
  };

  response.result.data.forEach((item) => {
    const period = item.period?.toLowerCase();
    const transformedData = transformPeriodData(item);

    if (period === "1y") {
      dataByYear["1Y"] = transformedData;
    } else if (period === "3y") {
      dataByYear["3Y"] = transformedData;
    } else if (period === "5y") {
      dataByYear["5Y"] = transformedData;
    }
  });

  return {
    success: true,
    message: response?.message ?? "Fetched risk ratio successfully",
    code: response?.code ?? null,

    // ✅ Root-level as_on_date
    as_on_date: asOnDate,

    dataByYear,
    requestId: response?.requestId ?? null,
  };
};


/* ================================
   Risk Adjusted Returns
================================ */
export const parseRiskAdjustedReturnsResponse = (response) => {
  if (!response?.result?.data || !Array.isArray(response.result.data)) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch risk-adjusted returns",
      code: response?.code ?? "UNKNOWN_ERROR",
      dataByYear: {
        "1Y": [],
        "3Y": [],
        "5Y": [],
      },
      as_on_date: response?.result?.as_on_date ?? null,   // ✅ FIXED
    };
  }

  const transformPeriodData = (periodData) => {
    const categories = [];

    if (periodData.sharpeRatio) {
      const sr = periodData.sharpeRatio;
      categories.push({
        category: "Sharpe Ratio",
        min: parseFloat(sr.categoryMin) || 0,
        max: parseFloat(sr.categoryMax) || 0,
        avg: parseFloat(sr.categoryAverage) || 0,
        current: parseFloat(sr.fundValue) || 0,
      });
    }

    if (periodData.information) {
      const ir = periodData.information;
      categories.push({
        category: "Information Ratio",
        min: parseFloat(ir.categoryMin) || 0,
        max: parseFloat(ir.categoryMax) || 0,
        avg: parseFloat(ir.categoryAverage) || 0,
        current: parseFloat(ir.fundValue) || 0,
      });
    }

    if (periodData.treynor) {
      const tr = periodData.treynor;
      categories.push({
        category: "Treynor Ratio",
        min: parseFloat(tr.categoryMin) || 0,
        max: parseFloat(tr.categoryMax) || 0,
        avg: parseFloat(tr.categoryAverage) || 0,
        current: parseFloat(tr.fundValue) || 0,
      });
    }

    if (periodData.sortinoRatio) {
      const sor = periodData.sortinoRatio;
      categories.push({
        category: "Sortino Ratio",
        min: parseFloat(sor.categoryMin) || 0,
        max: parseFloat(sor.categoryMax) || 0,
        avg: parseFloat(sor.categoryAverage) || 0,
        current: parseFloat(sor.fundValue) || 0,
      });
    }

    return categories;
  };

  const dataByYear = {
    "1Y": [],
    "3Y": [],
    "5Y": [],
  };

  response.result.data.forEach((item) => {
    const period = item.period?.toLowerCase();
    const transformedData = transformPeriodData(item);

    if (period === "1y") dataByYear["1Y"] = transformedData;
    else if (period === "3y") dataByYear["3Y"] = transformedData;
    else if (period === "5y") dataByYear["5Y"] = transformedData;
  });

  return {
    success: true,
    message: response?.message ?? "Fetched risk-adjusted returns successfully",
    code: response?.code ?? null,
    as_on_date: response?.result?.as_on_date ?? null,   // ✅ FIXED
    dataByYear,
  };
};



/* ================================
   Returns (Trailing & Calendar)
================================ */
export const parseReturnsResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch returns data",
      code: response?.code ?? "UNKNOWN_ERROR",
      trailingReturns: [],
      calendarReturns: [],
      as_on_date: null,
    };
  }

  const trailingReturnsData = Array.isArray(response?.result?.trailingReturns)
    ? response.result.trailingReturns
    : [];

  const calendarReturnsData = Array.isArray(response?.result?.calendarReturns)
    ? response.result.calendarReturns
    : [];

  // Transform trailingReturns: { period, fund, categoryAverage } -> { period, funds, categoryAverage }
  const trailingReturns = trailingReturnsData.map((item) => ({
    period: item?.period ?? '',
    funds: item?.fund != null && !isNaN(Number(item.fund)) ? Number(item.fund) : 0,
    categoryAverage: item?.categoryAverage != null && !isNaN(Number(item.categoryAverage)) ? Number(item.categoryAverage) : 0,
  }));

  // Transform calendarReturns: { year, fund, categoryAverage } -> { period, funds, categoryAverage }
  const calendarReturns = calendarReturnsData.map((item) => ({
    period: item?.year != null ? String(item.year) : '',
    funds: item?.fund != null && !isNaN(Number(item.fund)) ? Number(item.fund) : 0,
    categoryAverage: item?.categoryAverage != null && !isNaN(Number(item.categoryAverage)) ? Number(item.categoryAverage) : 0,
  }));

  return {
    success: true,
    message: response?.message ?? "Returns data fetched successfully",
    code: response?.code ?? null,
    trailingReturns,
    calendarReturns,
    as_on_date: response?.result?.as_on_date ?? null,
    requestId: response?.requestId ?? null,
  };
};

/* ================================
   Fund vs Category
================================ */
export const parseFundVsCategoryResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch fund vs category data",
      code: response?.code ?? "UNKNOWN_ERROR",
      data1Y: [],
      data3Y: [],
      fundId: null,
      scheme_id: null,
      asOnDate: null,   // 👈 added
      requestId: response?.requestId ?? null,
    };
  }

  const result = response.result;

  const data1Y = Array.isArray(result?.data1Y)
    ? result.data1Y.map((item) => {
      const metric = item?.metric ?? "";

      const fundValue =
        item?.fund != null && !isNaN(Number(item.fund))
          ? Number(item.fund)
          : null;

      const categoryAverage =
        item?.categoryAverage != null && !isNaN(Number(item.categoryAverage))
          ? Number(item.categoryAverage)
          : null;

      return {
        category: metric,
        fundValue,
        categoryAverage,
      };
    })
    : [];

  const data3Y = Array.isArray(result?.data3Y)
    ? result.data3Y.map((item) => {
      const metric = item?.metric ?? "";

      const fundValue =
        item?.fund != null && !isNaN(Number(item.fund))
          ? Number(item.fund)
          : null;

      const categoryAverage =
        item?.categoryAverage != null && !isNaN(Number(item.categoryAverage))
          ? Number(item.categoryAverage)
          : null;

      return {
        category: metric,
        fundValue,
        categoryAverage,
      };
    })
    : [];

  return {
    success: true,
    message: response?.message ?? "Fund vs category data retrieved successfully",
    code: response?.code ?? null,
    data1Y,
    data3Y,
    fundId: result?.fundId ?? null,
    scheme_id: result?.scheme_id ?? null,
    asOnDate: result?.as_on_date ?? null,   // 👈 added here
    requestId: response?.requestId ?? null,
  };
};


/* ================================
   Rolling Alpha
================================ */
export const parseRollingAlphaResponse = (response) => {
  const asOnDate = response?.result?.as_on_date ?? null;

  if (!response?.result) {
    const errorResponse = {
      success: false,
      message: response?.message ?? "Failed to fetch rolling alpha data",
      code: response?.code ?? "UNKNOWN_ERROR",
      data: [],
      minValue: null,
      maxValue: null,

      // ✅ Added
      as_on_date: null,

      requestId: response?.requestId ?? null,
    };

    // console.log("📉 Rolling Alpha Parser (Error):", errorResponse);
    return errorResponse;
  }

  const result = response.result;
  const monthlyAlpha = Array.isArray(result?.monthlyAlpha) ? result.monthlyAlpha : [];
  const maxCategory = Array.isArray(result?.maxCategory) ? result.maxCategory : [];
  const minCategory = Array.isArray(result?.minCategory) ? result.minCategory : [];

  const dataMap = new Map();

  // monthlyAlpha
  monthlyAlpha.forEach(([date, value]) => {
    if (date && value != null) {
      const dateStr = typeof date === "string" ? date : new Date(date).toISOString().split("T")[0];
      if (!dataMap.has(dateStr)) {
        dataMap.set(dateStr, { date: dateStr, monthlyAlpha: null, maxCategory: null, minCategory: null });
      }
      dataMap.get(dateStr).monthlyAlpha = Number(value) || 0;
    }
  });

  // maxCategory
  maxCategory.forEach(([date, value]) => {
    if (date && value != null) {
      const dateStr = typeof date === "string" ? date : new Date(date).toISOString().split("T")[0];
      if (!dataMap.has(dateStr)) {
        dataMap.set(dateStr, { date: dateStr, monthlyAlpha: null, maxCategory: null, minCategory: null });
      }
      dataMap.get(dateStr).maxCategory = Number(value) || 0;
    }
  });

  // minCategory
  minCategory.forEach(([date, value]) => {
    if (date && value != null) {
      const dateStr = typeof date === "string" ? date : new Date(date).toISOString().split("T")[0];
      if (!dataMap.has(dateStr)) {
        dataMap.set(dateStr, { date: dateStr, monthlyAlpha: null, maxCategory: null, minCategory: null });
      }
      dataMap.get(dateStr).minCategory = Number(value) || 0;
    }
  });

  const data = Array.from(dataMap.values())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((item) => {
      const dateObj = new Date(item.date);
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      return {
        year: `${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`,
        date: dateObj.getTime(),
        monthlyAlpha: item.monthlyAlpha ?? 0,
        maxCategory: item.maxCategory ?? 0,
        minCategory: item.minCategory ?? 0,
      };

    });

  const parsedResponse = {
    success: true,
    message: response?.message ?? "Rolling alpha data fetched successfully",
    code: response?.code ?? null,

    // ✅ Guaranteed as_on_date
    as_on_date: asOnDate,

    data,
    minValue: typeof result?.minValue === "number" ? result.minValue : null,
    maxValue: typeof result?.maxValue === "number" ? result.maxValue : null,
    requestId: response?.requestId ?? null,
  };

  // console.log("📊 Rolling Alpha Parser (Success):", response);
  return parsedResponse;
};


/* ================================
   Asset Allocation
================================ */
export const parseAssetAllocationResponse = (response) => {
  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch asset allocation",
      code: response?.code ?? "UNKNOWN_ERROR",
      assetAllocation: {
        equity: 0,
        debt: 0,
        cash: 0,
        others: 0,
      },
      equity: {
        marketCap: [],
      },
      debt: {
        creditRatings: [],
      },
      others: {
        breakdown: [],
      },
      monthwise: [],
      as_on_date: null,
    };
  }

  const result = response.result;

  // Extract as_on_date from API response
  // API structure: result.data.as_on_date
  const as_on_date = result?.data?.as_on_date ?? result?.as_on_date ?? null;

  // Check if we have monthwise data
  // API structure: result.data.monthwise
  const monthwiseData = result?.data?.monthwise;

  if (Array.isArray(monthwiseData) && monthwiseData.length > 0) {
    // Process monthwise data
    const monthwise = monthwiseData
      .map((month) => {
        const monthData = month?.data || {};

        // Parse asset allocation percentages
        const assetAllocation = {
          equity: parseFloat(monthData?.assetAllocation?.equity) || 0,
          debt: parseFloat(monthData?.assetAllocation?.debt) || 0,
          cash: parseFloat(monthData?.assetAllocation?.cash) || 0,
          others: parseFloat(monthData?.assetAllocation?.others) || 0,
        };

        // Parse equity market cap breakdown
        const equityMarketCap = Array.isArray(monthData?.equity?.marketCap)
          ? monthData.equity.marketCap.map((item) => ({
            name: item?.name || '',
            percentage: parseFloat(item?.percentage) || 0,
          }))
          : [];

        // Parse debt credit ratings
        const debtCreditRatings = Array.isArray(monthData?.debt?.creditRatings)
          ? monthData.debt.creditRatings.map((item) => ({
            name: item?.name || '',
            percentage: parseFloat(item?.percentage) || 0,
          }))
          : [];

        // Parse cash breakdown
        const cashBreakdown = Array.isArray(monthData?.cash?.breakdown)
          ? monthData.cash.breakdown.map((item) => ({
            name: item?.name || '',
            percentage: parseFloat(item?.percentage) || 0,
          }))
          : [];

        // Parse others breakdown
        const othersBreakdown = Array.isArray(monthData?.others?.breakdown)
          ? monthData.others.breakdown.map((item) => ({
            name: item?.name || '',
            percentage: parseFloat(item?.percentage) || 0,
          }))
          : [];

        return {
          label: month?.label ?? '',
          value: month?.value ?? null, // timestamp
          as_on_date: as_on_date, // Add as_on_date to each month item
          assetAllocation,
          equity: {
            marketCap: equityMarketCap,
          },
          debt: {
            creditRatings: debtCreditRatings,
          },
          cash: {
            breakdown: cashBreakdown,
          },
          others: {
            breakdown: othersBreakdown,
          },
          data: monthData, // Store original data for direct access
        };
      })
      .sort((a, b) => {
        // Sort by timestamp descending (latest first)
        const valA = a.value || 0;
        const valB = b.value || 0;
        return valB - valA;
      });

    // Use latest month's data for backward compatibility
    const latestMonth = monthwise[0] || {};

    return {
      success: true,
      message: response?.message ?? "Asset allocation fetched successfully",
      code: response?.code ?? null,
      assetAllocation: latestMonth.assetAllocation || {
        equity: 0,
        debt: 0,
        cash: 0,
        others: 0,
      },
      equity: latestMonth.equity || {
        marketCap: [],
      },
      debt: latestMonth.debt || {
        creditRatings: [],
      },
      cash: latestMonth.cash || {
        breakdown: [],
      },
      others: latestMonth.others || {
        breakdown: [],
      },
      monthwise: monthwise,
      as_on_date: as_on_date,
      requestId: response?.requestId ?? null,
    };
  }

  // Fallback to old structure (non-monthwise)
  // Parse asset allocation percentages
  const assetAllocation = {
    equity: parseFloat(result?.assetAllocation?.equity) || 0,
    debt: parseFloat(result?.assetAllocation?.debt) || 0,
    cash: parseFloat(result?.assetAllocation?.cash) || 0,
    others: parseFloat(result?.assetAllocation?.others) || 0,
  };

  // Parse equity market cap breakdown
  const equityMarketCap = Array.isArray(result?.equity?.marketCap)
    ? result.equity.marketCap.map((item) => ({
      name: item?.name || '',
      percentage: parseFloat(item?.percentage) || 0,
    }))
    : [];

  // Parse debt credit ratings
  const debtCreditRatings = Array.isArray(result?.debt?.creditRatings)
    ? result.debt.creditRatings.map((item) => ({
      name: item?.name || '',
      percentage: parseFloat(item?.percentage) || 0,
    }))
    : [];

  // Parse cash breakdown
  const cashBreakdown = Array.isArray(result?.cash?.breakdown)
    ? result.cash.breakdown.map((item) => ({
      name: item?.name || '',
      percentage: parseFloat(item?.percentage) || 0,
    }))
    : [];

  // Parse others breakdown
  const othersBreakdown = Array.isArray(result?.others?.breakdown)
    ? result.others.breakdown.map((item) => ({
      name: item?.name || '',
      percentage: parseFloat(item?.percentage) || 0,
    }))
    : [];

  return {
    success: true,
    message: response?.message ?? "Asset allocation fetched successfully",
    code: response?.code ?? null,
    assetAllocation,
    equity: {
      marketCap: equityMarketCap,
    },
    debt: {
      creditRatings: debtCreditRatings,
    },
    cash: {
      breakdown: cashBreakdown,
    },
    others: {
      breakdown: othersBreakdown,
    },
    monthwise: [],
    as_on_date: as_on_date,
    requestId: response?.requestId ?? null,
  };
};

/* ================================
   Fund Managers History
================================ */
export const parseFundManagersHistoryResponse = (response) => {

  if (!response?.result) {
    return {
      success: false,
      message: response?.message ?? "Failed to fetch fund managers history",
      code: response?.code ?? "UNKNOWN_ERROR",
      managers: [],
      requestId: response?.requestId ?? null,
    };
  }

  /* 
  console.log("parseFundManagersHistoryResponse RAW:", response); 
  */
  const managers = Array.isArray(response?.result?.managers)
    ? response.result.managers
    : Array.isArray(response?.result?.data)
      ? response.result.data
      : [];

  const data = managers;

  const parsedManagers = data.map((manager) => {
    const managerName = manager?.managerName ?? manager?.manager_name ?? manager?.name ?? "Manager Not Assigned";
    const startDate = manager?.startDate ?? manager?.manager_start_date ?? manager?.start_date ?? null;
    const endDate = manager?.endDate ?? manager?.manager_end_date ?? manager?.end_date ?? "PRESENT";
    const isActive = manager?.isActive ?? manager?.is_active ?? (endDate === "PRESENT" || !manager?.manager_end_date);

    return {
      managerName,
      startDate,
      endDate,
      isActive,
    };
  });

  console.log("parseFundManagersHistoryResponse PARSED:", parsedManagers);

  return {
    success: true,
    message: response?.message ?? "Fund managers history fetched successfully",
    code: response?.code ?? null,
    managers: parsedManagers,
    requestId: response?.requestId ?? null,
  };
};
